import PropTypes from "prop-types"
import React, { useCallback, useState } from "react"
import { useMarina } from "src/contexts/MarinaContext"
import PaymentStatusBadge from "src/main/Billing/Payments/PaymentStatusBadge"

import Form from "src/components/Form"
import Loader from "src/components/Loader"
import Modal from "src/components/Modal"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"

import DialogFooter from "./DialogFooter"
import DialogHeader from "./DialogHeader"
import { fillCopy, formatInvoiceDate } from "./copy"
import { usePayableContactInvoices } from "./hooks"

const SelectInvoicesDialog = ({
  contactId,
  copy,
  maxInvoicesPerPayment,
  onContinue,
  onClose,
}) => {
  const { marinaSlug, presentmentCurrency } = useMarina()
  const {
    data: invoices,
    isLoading,
    isError,
  } = usePayableContactInvoices({
    marinaSlug,
    contactId,
  })
  const [checkedIds, setCheckedIds] = useState(null)

  const openInvoices = invoices ?? []
  const checked =
    checkedIds ??
    new Set(
      openInvoices.slice(0, maxInvoicesPerPayment).map((invoice) => invoice.id)
    )
  const checkedInvoices = openInvoices.filter((invoice) =>
    checked.has(invoice.id)
  )
  const isAllChecked =
    openInvoices.length > 0 && checkedInvoices.length === openInvoices.length
  const isSomeChecked = checkedInvoices.length > 0 && !isAllChecked
  const isOverMax = checkedInvoices.length > maxInvoicesPerPayment

  const selectAllRef = useCallback(
    (checkbox) => {
      if (checkbox) {
        checkbox.indeterminate = isSomeChecked
      }
    },
    [isSomeChecked]
  )

  const formatMoney = (amount) =>
    formatCurrencyFromMinorUnits({ amount, currency: presentmentCurrency })

  const toggleInvoice = (invoiceId) => {
    const next = new Set(checked)
    if (next.has(invoiceId)) {
      next.delete(invoiceId)
    } else {
      next.add(invoiceId)
    }
    setCheckedIds(next)
  }

  const toggleAll = () => {
    setCheckedIds(
      new Set(isAllChecked ? [] : openInvoices.map((invoice) => invoice.id))
    )
  }

  const renderInvoices = () => {
    if (isLoading) {
      return <Loader />
    }
    if (isError) {
      return <Form.Error>{copy.selectInvoices.loadFailed}</Form.Error>
    }
    if (openInvoices.length === 0) {
      return <p className="m-0 text-gray-600">{copy.noOpenInvoices}</p>
    }
    return (
      <div className="flex flex-col gap-2">
        <div className="overflow-hidden rounded-lg border text-sm text-gray-900">
          <div className="flex h-10 items-center justify-between gap-4 bg-gray-200">
            <div className="min-w-0 flex-1 pl-4">
              <Form.Checkbox
                ref={selectAllRef}
                name="select-all-invoices"
                labelClassName="truncate pl-1.5"
                checked={isAllChecked}
                onChange={toggleAll}
              >
                <span className="font-semibold text-gray-900">
                  {copy.selectInvoices.selectAll}
                </span>
              </Form.Checkbox>
            </div>
            <span className="whitespace-nowrap px-4 font-semibold">
              {fillCopy(copy.entryPoints.selectionSummary, {
                count: checkedInvoices.length,
                amount: formatMoney(
                  checkedInvoices.reduce(
                    (sum, invoice) => sum + invoice.balance,
                    0
                  )
                ),
              })}
            </span>
          </div>
          <ul className="m-0 max-h-96 list-none overflow-y-auto p-0">
            {openInvoices.map((invoice) => (
              <li key={invoice.id} className="flex h-16 items-center border-t">
                <div className="min-w-0 flex-1 px-4 py-2">
                  <Form.Checkbox
                    name={`invoice-${invoice.id}`}
                    labelClassName="min-w-0 pl-1.5"
                    checked={checked.has(invoice.id)}
                    onChange={() => toggleInvoice(invoice.id)}
                  >
                    <span className="block truncate font-semibold text-gray-900">
                      {invoice.id}
                    </span>
                    <span className="mt-1 block truncate text-gray-900">
                      {invoice.sourceType}
                      {invoice.dueDate && (
                        <span className="text-gray-600">
                          {invoice.sourceType && " · "}
                          {formatInvoiceDate(invoice.dueDate)}
                        </span>
                      )}
                    </span>
                  </Form.Checkbox>
                </div>
                <div className="shrink-0 px-4 md:w-[236px]">
                  <PaymentStatusBadge payment={invoice} />
                </div>
                <div className="w-28 shrink-0 whitespace-nowrap px-4 text-right">
                  {formatMoney(invoice.balance)}
                </div>
              </li>
            ))}
          </ul>
        </div>
        {isOverMax && (
          <Form.Error>
            {fillCopy(copy.maxInvoices, { max: maxInvoicesPerPayment })}
          </Form.Error>
        )}
      </div>
    )
  }

  return (
    <Modal isOpen onClose={onClose} size="mediumFixed">
      <DialogHeader
        title={copy.selectInvoices.title}
        subtitle={copy.selectInvoices.subtitle}
      />
      <Modal.Body>
        <div className="pb-4 pt-8">{renderInvoices()}</div>
      </Modal.Body>
      <DialogFooter
        cancelText={copy.buttons.cancel}
        onCancel={onClose}
        confirmText={copy.buttons.continue}
        onConfirm={() => onContinue(checkedInvoices)}
        isConfirmDisabled={checkedInvoices.length === 0 || isOverMax}
      />
    </Modal>
  )
}

SelectInvoicesDialog.propTypes = {
  contactId: PropTypes.string.isRequired,
  copy: PropTypes.shape({
    buttons: PropTypes.shape({
      cancel: PropTypes.string.isRequired,
      continue: PropTypes.string.isRequired,
    }).isRequired,
    entryPoints: PropTypes.shape({
      selectionSummary: PropTypes.string.isRequired,
    }).isRequired,
    maxInvoices: PropTypes.string.isRequired,
    noOpenInvoices: PropTypes.string.isRequired,
    selectInvoices: PropTypes.shape({
      loadFailed: PropTypes.string.isRequired,
      selectAll: PropTypes.string.isRequired,
      subtitle: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  maxInvoicesPerPayment: PropTypes.number.isRequired,
  onContinue: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default SelectInvoicesDialog
